"use client"

import { GiftIcon } from "lucide-react"
import type { MembersHomeBirthday } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import { calendarDayToDate, fullName, shortName } from "@/features/dashboard/home-d4/lib/home-format"
import { MemberAvatar, whatsappHref } from "@/features/members/components/members-views"
import { useLocale, useTranslations } from "@/i18n"

/** Whose birthday a "send a message" link should go to: today's first, else the next one reachable on WhatsApp. */
export function birthdayMessageTarget(birthdays: MembersHomeBirthday[]) {
  const reachable = birthdays.filter((person) => person.phone && whatsappHref(person.phone))
  return reachable.find((person) => person.isToday) ?? reachable[0] ?? null
}

/** Birthdays (D4): this week's birthdays with the day and the age turned, and a WhatsApp message link. */
export function BirthdaysTile({ birthdays, total }: { birthdays: MembersHomeBirthday[]; total: number }) {
  const t = useTranslations("dashboard.home.birthdays")
  const locale = useLocale()
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" })
  const target = birthdayMessageTarget(birthdays)
  const targetHref = target?.phone ? whatsappHref(target.phone) : null

  return (
    <HomeTile title={t("title")} subtitle={t("subtitle")} testId='home-tile-birthdays'>
      {birthdays.length === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <>
          <ul className='grid grid-cols-2 gap-x-3 gap-y-2.5'>
            {birthdays.map((person) => {
              const day = person.isToday ? t("today") : weekday.format(calendarDayToDate(person.date))
              return (
                <li key={person.id} data-testid='birthday-row' className='flex min-w-0 items-center gap-2'>
                  <MemberAvatar
                    member={{ name: fullName(person.firstName, person.lastName), avatarUrl: person.avatarUrl }}
                    className='size-8 text-[11px]'
                  />
                  <span className='flex min-w-0 flex-col'>
                    <span className='truncate text-[12.5px] font-bold'>{shortName(person.firstName, person.lastName)}</span>
                    <span className='truncate text-[11.5px] text-muted-foreground'>{t(person.age === 1 ? "dayAgeOne" : "dayAgeOther", { day, age: person.age })}</span>
                  </span>
                </li>
              )
            })}
          </ul>
          {total > birthdays.length ? (
            <span className='text-[11.5px] text-muted-foreground'>{t(total - birthdays.length === 1 ? "moreOne" : "moreOther", { count: total - birthdays.length })}</span>
          ) : null}
          {target && targetHref ? (
            <a
              href={targetHref}
              target='_blank'
              rel='noreferrer'
              className='mt-auto inline-flex items-center gap-1.5 self-start rounded-md text-[13px] font-bold text-primary hover:underline'
            >
              <GiftIcon aria-hidden className='size-[15px]' />
              {t("sendMessage", { name: target.firstName })}
            </a>
          ) : null}
        </>
      )}
    </HomeTile>
  )
}
